import { isTerminalQueueItemStatus } from 'api/optJobs/optJobs.api'
import { QueueItem, QueueItemStatus } from 'api/queueItems/queueItems.types'
import { OptimizationStep, StepId, StepState } from './OptJobPhaseProgress.types'

type StepDef = { id: StepId; labelKey: string; descriptionKey: string }

// `labelKey`/`descriptionKey` are raw i18n keys; call sites translate at render time.
export const STEPS: readonly StepDef[] = [
  {
    id: 'preparing',
    labelKey: 'Input processing',
    descriptionKey: 'The part of the optimization where the backend prepared the job.',
  },
  { id: 'optimizing', labelKey: 'Optimizing', descriptionKey: 'Solver is running.' },
  {
    id: 'processing',
    labelKey: 'Result processing',
    descriptionKey: 'Finalizing and storing results.',
  },
] as const

const StepIdToIndex: Record<StepId, number> = Object.fromEntries(
  STEPS.map((step, index) => [step.id, index])
) as Record<StepId, number>

/**
 * The single source of truth for which phase a queue item's coarse `status`
 * implies is currently active (not yet done). Both the expanded step list's
 * timestamp-free fallback and the collapsed header's step count derive from
 * this — do not add another status→phase mapping elsewhere.
 *
 * The backend's `status` lifecycle runs `On hold` → `Waiting` →
 * `InputProcessing` → `Running` → `Solved` → `ResultProcessing` → `Finished`
 * (confirmed order); values below must stay non-decreasing across that
 * sequence so the step count never regresses.
 */
const QUEUE_ITEM_STATUS_ACTIVE_STEP: Record<Exclude<QueueItemStatus, undefined>, StepId> = {
  'On hold': 'preparing',
  Waiting: 'preparing',
  InputProcessing: 'preparing',
  Running: 'optimizing',
  Solved: 'processing',
  ResultProcessing: 'processing',
  Finished: 'processing',
  Failed: 'processing',
  Unsolvable: 'processing',
}

function getActiveStepIndex(status: QueueItemStatus): number {
  if (status == null) return -1
  return StepIdToIndex[QUEUE_ITEM_STATUS_ACTIVE_STEP[status]]
}

/**
 * Pure projection from a queue item's coarse `status` to the shared 3-phase
 * step list. Per-phase durations are out of scope; each step carries only its
 * `state` (`pending` / `active` / `done`).
 *
 * `Failed` / `Unsolvable` yield no progress at all: the status says the item
 * died but not where, so every phase is left `pending` and the failing one is
 * attributed from the timestamps by `deriveQueueItemPhaseDurations`.
 */
export function deriveStepsFromStatus(status: QueueItemStatus): OptimizationStep[] {
  if (status === 'Finished') {
    return STEPS.map((step) => ({
      id: step.id,
      labelKey: step.labelKey,
      descriptionKey: step.descriptionKey,
      state: 'done',
    }))
  }

  const activeIndex = status === 'Failed' || status === 'Unsolvable' ? -1 : getActiveStepIndex(status)

  return STEPS.map((step, index) => {
    let state: StepState = 'pending'
    if (index < activeIndex) {
      state = 'done'
    } else if (index === activeIndex) {
      state = 'active'
    }

    return {
      id: step.id,
      labelKey: step.labelKey,
      descriptionKey: step.descriptionKey,
      state,
    }
  })
}

/**
 * Ordered `[start, end)` boundary timestamps per phase. The `end` of phase `i`
 * is the `start` of phase `i + 1`, so the four timestamps below define the
 * three phase intervals. Any of the intermediate stamps may be null (backend
 * has not emitted it yet, or the backend skipped/collapsed a phase);
 * `created_at` is always set.
 */
type PhaseBoundaries = readonly [
  createdAt: number,
  queuedAt: number | null,
  finishedAt: number | null,
  resultProcessedAt: number | null,
]

function toMs(iso: string | null): number | null {
  if (iso == null) return null
  const ms = new Date(iso).getTime()
  return Number.isFinite(ms) ? ms : null
}

function collectBoundaries(item: QueueItem): PhaseBoundaries {
  return [
    new Date(item.created_at).getTime(),
    toMs(item.queued_at),
    toMs(item.finished_at),
    toMs(item.result_processed_at),
  ]
}

/**
 * Finds the next non-null boundary at or after `fromIndex`. Used to fall back
 * a completed phase's `end` to the next available timestamp when the direct
 * boundary is missing (backend skipped/collapsed a stamp) so a done phase
 * never renders as active.
 */
function nextBoundaryAtOrAfter(boundaries: PhaseBoundaries, fromIndex: number): number | null {
  for (let index = fromIndex; index < boundaries.length; index += 1) {
    const value = boundaries[index]
    if (value != null) return value
  }
  return null
}

function hasAnyPhaseTimestamp(boundaries: PhaseBoundaries): boolean {
  for (let index = 1; index < boundaries.length; index += 1) {
    if (boundaries[index] != null) return true
  }
  return false
}

/**
 * The moment a terminal queue item reached its end state. Terminal statuses are
 * sticky, so `status_updated_at` closes its whole life cycle — and for an item
 * that died before the phase it was in could be stamped, it is the only closing
 * signal there is. Falls back to the last phase boundary for rows predating the
 * field.
 */
function terminalEndMs(item: QueueItem, boundaries: PhaseBoundaries): number | null {
  return toMs(item.status_updated_at) ?? boundaries[3] ?? boundaries[2] ?? null
}

export type QueueItemPhaseProgress = {
  steps: OptimizationStep[]
  overallElapsedMs: number
}

/**
 * For a failed item, or an Unsolvable item without queued_at, attribute the stop
 * to the first phase whose end boundary was never stamped. Earlier phases ran
 * to completion, so the stop has to be at or after the first gap.
 *
 * When every boundary is stamped, attribute the stop to optimizing: the final
 * result-processing stamp alone does not identify where the error occurred.
 */
function deriveStoppedStepIndex(boundaries: PhaseBoundaries): number {
  for (let index = 0; index < STEPS.length; index += 1) {
    if (boundaries[index + 1] == null) return index
  }
  return StepIdToIndex.optimizing
}

/**
 * Attribute the stop to `stoppedIndex`; later phases inherit the terminal state
 * without a duration. Only the attributed phase is timed to the item's terminal
 * status update when its own end boundary was never stamped.
 */
function markStoppedFrom(
  steps: OptimizationStep[],
  stoppedIndex: number,
  boundaries: PhaseBoundaries,
  terminalEnd: number,
  state: 'failed' | 'unsolvable'
): void {
  const stoppedStep = steps[stoppedIndex]
  if (stoppedStep == null) return

  const startMs = boundaries[stoppedIndex]
  const endMs = boundaries[stoppedIndex + 1] ?? terminalEnd
  stoppedStep.state = state
  stoppedStep.durationMs = startMs == null ? undefined : Math.max(0, endMs - startMs)

  for (let index = stoppedIndex + 1; index < steps.length; index += 1) {
    steps[index].state = state
    steps[index].durationMs = undefined
  }
}

function deriveStepsFromBoundaries(boundaries: PhaseBoundaries, now: number): OptimizationStep[] {
  return STEPS.map((step, index) => {
    const start = boundaries[index]
    const directEnd = boundaries[index + 1]
    const laterEnd = directEnd ?? nextBoundaryAtOrAfter(boundaries, index + 2)

    let state: StepState = 'pending'
    let durationMs: number | undefined

    if (start != null && laterEnd != null) {
      state = 'done'
      durationMs = Math.max(0, laterEnd - start)
    } else if (start != null) {
      state = 'active'
      durationMs = Math.max(0, now - start)
    } else if (laterEnd != null) {
      state = 'done'
      durationMs = 0
    }

    return {
      id: step.id,
      labelKey: step.labelKey,
      descriptionKey: step.descriptionKey,
      state,
      durationMs,
    }
  })
}

/**
 * Pure, timestamp-driven derivation of the 3-phase step list for a single
 * queue item, including per-phase durations and the overall elapsed clock.
 *
 * State per phase is derived from the `[start, end)` null pattern:
 * - both set → `done`, `durationMs = end − start` (with `end` falling back to
 *   the next available later boundary when the direct one is missing),
 * - start set, no later boundary set → `active`, `durationMs = now − start`
 *   (the last non-null boundary — this is what ticks live),
 * - start null but a later boundary is set → `done` with `durationMs = 0`
 *   (the backend skipped/collapsed the boundary stamp),
 * - start null and no later boundary set → `pending`, no duration.
 *
 * For terminal statuses the derivation is finalized: `Finished` marks any
 * still-active phase as `done`; `Failed` / `Unsolvable` mark the stopped phase
 * and every phase after it with their terminal state, timing only the stopped
 * phase. Unsolvable with queued_at is attributed to optimizing; other stops
 * use `deriveStoppedStepIndex`.
 *
 * `jobHasEnded` finalizes an item whose own status never became terminal: the job it
 * belongs to stopped, so the item stopped with it, wherever it had got to.
 *
 * If no phase timestamps are present beyond `created_at`, falls back to the
 * coarse `deriveStepsFromStatus(status)` mapping so a brand-new item still
 * renders sensibly.
 */
export function deriveQueueItemPhaseDurations(
  item: QueueItem,
  now: number,
  jobHasEnded = false
): QueueItemPhaseProgress {
  const boundaries = collectBoundaries(item)
  const createdAtMs = boundaries[0]
  // A job that has ended stops producing stamps for its items, so an item still
  // sitting in a non-terminal status was abandoned rather than left running — and
  // an abandoned item did not finish, which makes it a failure like any other.
  const terminal = isTerminalQueueItemStatus(item.status) || jobHasEnded
  let terminalFailureState: 'failed' | 'unsolvable' | null = null
  if (terminal && item.status !== 'Finished') {
    terminalFailureState = item.status === 'Unsolvable' ? 'unsolvable' : 'failed'
  }
  const overallEndMs = terminal ? (terminalEndMs(item, boundaries) ?? createdAtMs) : now

  const steps = hasAnyPhaseTimestamp(boundaries)
    ? deriveStepsFromBoundaries(boundaries, now)
    : deriveStepsFromStatus(item.status)

  if (!hasAnyPhaseTimestamp(boundaries)) {
    const activeStep = steps.find((step) => step.state === 'active')
    if (activeStep != null) {
      activeStep.durationMs = Math.max(0, now - createdAtMs)
    }
  }

  if (terminal && terminalFailureState == null) {
    for (const step of steps) {
      if (step.state === 'active') {
        step.state = 'done'
      }
    }
  }

  if (terminalFailureState != null) {
    // Unsolvable is a solver outcome. Without queued_at, older or incomplete rows
    // cannot time the solve, so attribution falls back to the first unstamped phase.
    const stoppedStepIndex =
      terminalFailureState === 'unsolvable' && boundaries[1] != null
        ? StepIdToIndex.optimizing
        : deriveStoppedStepIndex(boundaries)
    markStoppedFrom(steps, stoppedStepIndex, boundaries, overallEndMs, terminalFailureState)
  }

  return { steps, overallElapsedMs: Math.max(0, overallEndMs - createdAtMs) }
}

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n: number): string => n.toString().padStart(2, '0')
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
  return `${pad(m)}:${pad(s)}`
}
